import { ResponsiveContainer, CartesianGrid, Tooltip as ChartTooltip, Line, Bar, BarChart, XAxis, YAxis } from 'recharts';
import { type ActivityChartData, getActivityChartData, dayToDate } from '../services/analytics';

type DashboardActivityChartProps = {
  chartData: ActivityChartData;
  screenMode: 'small' | 'medium' | 'large';
};

export function DashboardActivityChart({ chartData, screenMode }: DashboardActivityChartProps) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        tabIndex={-1}
        data={chartData}
        barSize={8}
        margin={{
          top: 5,
          right: -25,
          bottom: 0,
          left: -25,
        }}
        style={{
          fontFamily: 'inherit',
          fontSize: '12px',
        }}
      >
        <CartesianGrid stroke="#d0d4e4" strokeDasharray="1 2" />
        <XAxis
          dataKey="day"
          interval={2}
          tickFormatter={(day: string) => {
            const date = dayToDate(day);

            return date.toLocaleDateString('en-US', {
              month: 'short', // "Nov"
              day: 'numeric', // "15"
            });
          }}
          padding={{
            left: screenMode === 'small' ? 16 : 32,
            right: screenMode === 'small' ? 16 : 32,
          }}
        />
        <YAxis yAxisId="left" interval={0} />
        <YAxis yAxisId="right" orientation="right" interval={0} />
        <ChartTooltip
          cursor={{ fill: 'rgba(0, 0, 0, 0.03)' }}
          wrapperStyle={{
            boxShadow: '0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
            fontFamily: 'inherit',
            fontSize: 12,
          }}
          itemSorter={(item) => {
            switch (item.dataKey) {
              case 'loginsCount':
                return 0;
              case 'errorRequestsCount':
                return 1;
              case 'successRequestsCount':
                return 2;
              default:
                return 100;
            }
          }}
          contentStyle={{
            backgroundColor: '#fff',
            borderRadius: 4,
            border: '1px solid #e0e0e0',
            padding: '10px 10px 6px 10px',
            lineHeight: '0.8',
          }}
          labelStyle={{
            marginBottom: 6,
            fontWeight: 600,
          }}
          formatter={(value, name) => {
            if (name === 'loginsCount') {
              return [`${value}`, 'Total client logins'];
            }
            if (name === 'successRequestsCount') {
              return [`${value}`, 'Successful requests'];
            }
            if (name === 'errorRequestsCount') {
              return [`${value}`, 'Requests with errors'];
            }

            return [value, name];
          }}
          labelFormatter={(label) => {
            // recharts types the label as ReactNode; here it is always the `day` string of the data point.
            const date = dayToDate(label as string);
            return date.toLocaleDateString('en-US', {
              month: 'long', // "November"
              day: 'numeric', // "15"
              year: 'numeric', // "2025"
            });
          }}
          separator=" → "
        />
        <Bar yAxisId="right" stackId="a" isAnimationActive={false} dataKey="successRequestsCount" fill="#c4c8d8ff" />
        <Bar yAxisId="right" stackId="a" isAnimationActive={false} dataKey="errorRequestsCount" fill="#ffcb00" />
        <Line
          isAnimationActive={false}
          yAxisId="left"
          dataKey="loginsCount"
          fill="#0073ea"
          stroke="#0073ea"
          strokeWidth={1}
          dot={{ r: 3 }}
          activeDot={{ r: 6 }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}
